/*
 * Updates (chat 11).
 *
 * Web: a deploy ships a new sw.js. The browser installs it in the background (on a check when the app comes back
 * to the front, every 30 min, or at a start); it then takes over this page, which still runs the old code. The new
 * version is used by reloading at a moment nobody notices: while the tab is in the background with nothing waiting
 * to sync, or within the first seconds after opening, before any click or key. (Before this, a tab left open kept
 * its version for days: the owner's feedback of 11 Oct came from a build a deploy old.)
 *
 * Android: the server says what's newest (apps/server/src/updates.ts); native code downloads and serves it
 * (apps/android/.../Updater.kt). This side decides when:
 *  - New screens (a web bundle) download in the background and are used at the next start, but only if the app
 *    was left with nothing waiting to sync: it "arms" the update while hidden with an empty outbox, and disarms it
 *    when it comes back. Android keeps a left app running (Home and Back only minimize it), so the next start can
 *    be days away: coming back after 10 minutes or more with the update armed switches to it there and then, on
 *    the page that was open (chat 13). With "Ask before updating" on, it waits for the owner's "Restart" instead.
 *  - The first start of a new bundle reports ready once the app is up; otherwise native code rolls it back.
 *  - A bundle that needs newer native code than this app has waits for the app update (APK), offered in a banner
 *    and in Settings; Android's installer asks to confirm, and the notes stay (same signing key, same origin).
 */
import { App as CapApp } from "@capacitor/app";
import { create } from "zustand";
import { apiFetch, getToken, isNative } from "./api";
import { db, getSyncState, onSyncState } from "./db/client";
import { EngramNative, type UpdaterInfo } from "./native";
import { usePrefs } from "./ui/prefs";

export interface BundleRelease {
  id: string;
  sha256: string;
  size: number;
  minNative: number;
  created: number;
  build?: string;
}
export interface ApkRelease {
  versionCode: number;
  versionName: string;
  sha256: string;
  size: number;
  native: number;
  created: number;
}

interface UpdateState {
  info: UpdaterInfo | null;
  checked: number | null;
  checking: boolean;
  /** none: up to date · downloading · ready: waits for the next start · needs-app: needs the app update first */
  bundle: "none" | "downloading" | "ready" | "needs-app";
  bundleBuild: string | null;
  /** A newer app to install, if any. */
  apk: ApkRelease | null;
  apkState: "idle" | "downloading" | "installing" | "permission";
  error: string | null;
}

export const useUpdates = create<UpdateState>(() => ({
  info: null,
  checked: null,
  checking: false,
  bundle: "none",
  bundleBuild: null,
  apk: null,
  apkState: "idle",
  error: null,
}));
const set = useUpdates.setState;

const CHECK_EVERY = 10 * 60_000;
const loadedAt = Date.now();
/** An app install waiting for "allow installs from Engram" (allowing it restarts the app; carry on after). */
const INSTALL_KEY = "engram.apkInstall";
const pendingInstall = {
  get: () => {
    try {
      return Number(localStorage.getItem(INSTALL_KEY)) || 0;
    } catch {
      return 0;
    }
  },
  set: (code: number | null) => {
    try {
      if (code) localStorage.setItem(INSTALL_KEY, String(code));
      else localStorage.removeItem(INSTALL_KEY);
    } catch {
      /* storage unavailable: the owner taps Update app again */
    }
  },
};
let readySent = false;
let started = false;
let armed = false;

/** Away at least this long, an armed update is used on coming back (test builds can shorten it). */
const APPLY_AFTER_AWAY = 10 * 60_000;
function applyAfterAway(): number {
  if (import.meta.env.VITE_TEST_HOOKS) {
    try {
      const ms = Number(localStorage.getItem("engram.test.awayMs"));
      if (ms > 0) return ms;
    } catch {
      /* the default */
    }
  }
  return APPLY_AFTER_AWAY;
}

/** Switching screens loads the app's start page: remember the page that was open, to reopen it. */
const ROUTE_KEY = "engram.updateRoute";
function rememberRoute(on: boolean) {
  try {
    if (on) sessionStorage.setItem(ROUTE_KEY, JSON.stringify({ path: location.pathname + location.search + location.hash, at: Date.now() }));
    else sessionStorage.removeItem(ROUTE_KEY);
  } catch {
    /* storage unavailable: it opens on Home, like a cold start */
  }
}
// At load, before the first render: back on that page.
if (isNative) {
  try {
    const saved = sessionStorage.getItem(ROUTE_KEY);
    sessionStorage.removeItem(ROUTE_KEY);
    const r = saved ? (JSON.parse(saved) as { path?: unknown; at?: unknown }) : null;
    if (r && typeof r.path === "string" && r.path.startsWith("/") && Date.now() - Number(r.at) < 60_000 && location.pathname === "/") {
      history.replaceState(null, "", r.path);
    }
  } catch {
    /* Home */
  }
}

/** Switches to the waiting bundle now: the page reloads into it (on trial) and reopens where it was. */
async function switchNow(): Promise<boolean> {
  rememberRoute(true);
  const r = await EngramNative.applyUpdateNow().catch(() => ({ applied: false }));
  if (!r.applied) rememberRoute(false);
  return r.applied;
}

/** The app is on screen (the sign-in screen, or the app with its replica open): a new bundle's trial passed. */
export function markAppReady() {
  if (!isNative || readySent) return;
  readySent = true;
  // A moment later, and only if something was drawn: a bundle whose screens crash right after the first paint
  // shouldn't count as working.
  setTimeout(() => {
    if (!document.getElementById("root")?.childElementCount) return;
    void EngramNative.appReady()
      .then((info) => set({ info }))
      .catch(() => undefined);
  }, 1500);
}

/** Signed in: look for updates now and then, and arm a waiting one whenever the app is left with nothing to sync. */
export function startUpdateChecks() {
  if (!isNative || started) return;
  started = true;
  // Started again after "allow installs from Engram" (Android restarts the app when it's allowed): carry on.
  const resumeInstall = pendingInstall.get();
  setTimeout(() => void checkForUpdates().then(() => {
    const { apk } = useUpdates.getState();
    pendingInstall.set(null);
    if (resumeInstall && apk?.versionCode === resumeInstall) void installAppUpdate(false);
  }), resumeInstall ? 1000 : 5000);
  void CapApp.addListener("resume", () => {
    const { checked, apkState } = useUpdates.getState();
    // Back from that setting without a restart (not allowed, or an Android that doesn't restart apps).
    if (apkState === "permission") void installAppUpdate(false);
    else if (!checked || Date.now() - checked > CHECK_EVERY) void checkForUpdates();
  });
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      void armIfSafe();
    } else if (armed) {
      armed = false;
      const disarm = () => void EngramNative.armUpdate({ on: false }).then((info) => set({ info }), () => undefined);
      // Back after a while (wall clock: a frozen or sleeping phone counts as away), nothing typed since: switch now.
      if (Date.now() - hiddenAt >= applyAfterAway() && getSyncState().pending === 0) {
        void switchNow().then((applied) => {
          if (!applied) disarm();
        });
      } else disarm();
    }
  });
  // Changes that were still being sent when the app was left: arm once they're through.
  onSyncState((s) => {
    if (s.pending === 0 && document.visibilityState === "hidden") void armIfSafe();
  });
}

export async function checkForUpdates(): Promise<void> {
  if (!isNative) return checkWeb();
  if (!getToken() || useUpdates.getState().checking) return;
  set({ checking: true, error: null });
  try {
    const info = useUpdates.getState().info ?? (await EngramNative.updaterInfo());
    const r = await apiFetch("/app/latest");
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const latest = (await r.json()) as { bundle: BundleRelease | null; apk: ApkRelease | null };
    const apk = latest.apk && latest.apk.versionCode > info.versionCode ? latest.apk : null;
    set({ info, apk, checked: Date.now() });
    const b = latest.bundle;
    // Newer than what runs (never back to an older build, e.g. under an APK built after the last deploy), and not
    // one that failed to start here before.
    if (!b || b.id === __APP_BUNDLE__ || b.created <= __APP_BUILD_TIME__ || info.failed.includes(b.id)) {
      set({ bundle: "none", bundleBuild: null });
    } else if (b.minNative > info.native) {
      set({ bundle: "needs-app", bundleBuild: b.build ?? b.id });
    } else if (info.next === b.id) {
      set({ bundle: "ready", bundleBuild: b.build ?? b.id });
    } else {
      set({ bundle: "downloading", bundleBuild: b.build ?? b.id });
      const after = await EngramNative.downloadBundle({ id: b.id, sha256: b.sha256 });
      set({ info: after, bundle: "ready" });
    }
  } catch (e) {
    set({ error: e instanceof Error ? e.message : String(e), bundle: useUpdates.getState().bundle === "downloading" ? "none" : useUpdates.getState().bundle });
  } finally {
    set({ checking: false });
  }
}

/** Saves what's being typed and sends what waits; true when nothing is left to sync. */
async function flushed(): Promise<boolean> {
  await db.saveAll(1500);
  if (getSyncState().pending === 0) return true;
  const s = await db.sync().catch(() => getSyncState());
  return s.pending === 0;
}

async function armIfSafe() {
  if (useUpdates.getState().bundle !== "ready" || usePrefs.getState().askBeforeUpdating || armed) return;
  if (!(await flushed()) || document.visibilityState !== "hidden") return;
  armed = true;
  const info = await EngramNative.armUpdate({ on: true }).catch(() => null);
  if (info) set({ info });
}

/** "Restart": switch to the waiting version now, once every change has reached the server. */
export async function restartToUpdate(): Promise<void> {
  set({ error: null });
  if (!(await flushed())) {
    set({ error: `${getSyncState().pending} change(s) haven't synced yet. The update waits until they have.` });
    return;
  }
  if (!isNative) return location.reload();
  if (!(await switchNow())) set({ error: "The update isn't ready any more. Check again." });
}

/** `ask`: if installs from Engram aren't allowed yet, open Android's setting (a tap) or just say so (a retry). */
export async function installAppUpdate(ask = true): Promise<void> {
  const { apk } = useUpdates.getState();
  if (!apk) return;
  set({ error: null, apkState: "downloading" });
  try {
    await EngramNative.downloadApk({ versionCode: apk.versionCode, sha256: apk.sha256 });
    // Changes typed just before should be on the server before Android replaces the app.
    await flushed();
    set({ apkState: "installing" });
    const r = await EngramNative.installApk({ versionCode: apk.versionCode, ask });
    if (r.status === "permission" && ask) pendingInstall.set(apk.versionCode);
    set({ apkState: r.status === "permission" ? "permission" : "idle" });
  } catch (e) {
    set({ apkState: "idle", error: e instanceof Error ? e.message : String(e) });
  }
}

// ---- Web: the service worker (see the top of this file) ----

let webReady = false;
let touched = false;
let webStarted = false;

/** Called once at startup in production web builds. */
export function startWebUpdates() {
  if (isNative || webStarted || !import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  webStarted = true;
  const sw = navigator.serviceWorker;
  // A first visit's service worker taking over isn't an update: the page already runs that version.
  let controlled = !!sw.controller;
  const mark = () => (touched = true);
  addEventListener("pointerdown", mark, { capture: true, once: true });
  addEventListener("keydown", mark, { capture: true, once: true });
  sw.addEventListener("controllerchange", () => {
    if (!controlled) {
      controlled = true;
      return;
    }
    webReady = true;
    set({ bundle: "ready", bundleBuild: null, checked: Date.now() });
    // Its build.json is in its cache: say which version is waiting.
    void fetch("/build.json")
      .then((r) => r.json() as Promise<{ label?: string }>)
      .then((b) => set({ bundleBuild: b.label ?? null }))
      .catch(() => undefined);
    void applyWebIfSafe();
  });
  void sw.ready.then((reg) =>
    reg.addEventListener("updatefound", () => {
      if (controlled && !webReady) set({ bundle: "downloading" });
    }),
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void applyWebIfSafe();
    else if (Date.now() - (useUpdates.getState().checked ?? loadedAt) > CHECK_EVERY) void checkWeb();
  });
  onSyncState((s) => {
    if (s.pending === 0 && document.visibilityState === "hidden") void applyWebIfSafe();
  });
  setInterval(() => {
    if (document.visibilityState === "visible") void checkWeb();
  }, 30 * 60_000);
}

async function checkWeb() {
  if (isNative || !("serviceWorker" in navigator) || useUpdates.getState().checking) return;
  set({ checking: true, error: null });
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    await reg?.update();
  } catch (e) {
    set({ error: e instanceof Error ? e.message : String(e) });
  } finally {
    set({ checking: false, checked: Date.now() });
  }
}

/** Reload into the new version while nobody's looking: tab hidden (or just opened, untouched) and all synced. */
async function applyWebIfSafe() {
  if (!webReady || usePrefs.getState().askBeforeUpdating) return;
  const early = () => !touched && Date.now() - loadedAt < 10_000;
  if (document.visibilityState !== "hidden" && !early()) return;
  if (!(await flushed())) return;
  if (document.visibilityState === "hidden" || early()) location.reload();
}
